import 'rc-slider/assets/index.css';

import Slider from 'rc-slider';
import React, { useEffect, useRef, useState } from 'react';
import { BiChevronsDown, BiPause, BiPlay, BiSolidVideoOff, BiSolidVolumeFull, BiSolidVolumeMute, BiX } from 'react-icons/bi';
import { RiArrowLeftDownLine, RiArrowRightDownLine } from 'react-icons/ri';

import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';

import { IAiAppearance } from '../types';
import BiLouiseSmallGray from './icons/BiLouiseSmallGray';
import { widgetUtils } from './utils';

const VideoWidget = ({
  data: appearance,
  messages,
}: {
  data: IAiAppearance;
  messages: {
    userType: 'bot' | 'user';
    data: { type: string; value: any };
  }[];
}) => {
  const lastMessage = messages.at(-1);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showActionButtons, setShowActionButtons] = useState(appearance.alwaysShowButtons ?? false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInVideoFailureMode, setIsInVideoFailureMode] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const maxRetries = 3;

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (value: number) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleMuteUnmute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    setIsLoading(true);
    setIsInVideoFailureMode(false);
    setIsRetrying(true);

    if (videoRef.current) videoRef.current.src = 'https://cdn.1cdn.app/user/QN1N348R4LLTF1RMH6C2/24071606212410_welcome.mp4';
  };

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      video.addEventListener('playing', () => {
        setIsPlaying(true);
      });
      video.addEventListener('loadedmetadata', () => {
        setDuration(video.duration);
      });
      video.addEventListener('timeupdate', () => {
        setCurrentTime(video.currentTime);

        // const timeRemaining = video.duration - video.currentTime;

        // if (!appearance.alwaysShowButtons && timeRemaining <= 5)
        //   setShowActionButtons(true);
      });
      video.addEventListener('ended', () => {
        setIsPlaying(false);
      });
    }

    return () => {
      if (video) {
        video.removeEventListener('playing', () => {
          return;
        });
        video.removeEventListener('loadedmetadata', () => {
          return;
        });
        video.removeEventListener('timeupdate', () => {
          return;
        });
        video.addEventListener('ended', () => {
          return;
        });
      }
    };
  }, [
    // appearance.alwaysShowButtons,
    isPlaying,
    videoRef,
  ]);

  useEffect(() => {
    const video = videoRef.current;

    const handleCanPlayThrough = () => {
      setIsLoading(false);
      setIsInVideoFailureMode(false);
      setIsRetrying(false);
    };

    const handleError = () => {
      if (retryCount < maxRetries) {
        setRetryCount(retryCount + 1);
        setIsLoading(true);
        setIsRetrying(true);

        if (video) video.src = 'https://cdn.1cdn.app/user/QN1N348R4LLTF1RMH6C2/24071606212410_welcome.mp4';
      } else {
        setIsInVideoFailureMode(true);
        setIsLoading(false);
        setIsRetrying(false);
      }
    };

    video?.addEventListener('canplaythrough', handleCanPlayThrough);
    video?.addEventListener('error', handleError);

    return () => {
      video?.removeEventListener('canplaythrough', handleCanPlayThrough);
      video?.removeEventListener('error', handleError);
    };
  }, [retryCount, videoRef, maxRetries]);

  useEffect(() => {
    setShowActionButtons(appearance.alwaysShowButtons);
  }, [appearance.alwaysShowButtons]);

  return (
    <div className="tw-w-full tw-h-screen tw-max-w-[360px] tw-max-h-[600px]  tw-bg-gray-800 tw-rounded-2xl tw-border tw-border-black tw-relative">
      {appearance?.customCss && <style dangerouslySetInnerHTML={{ __html: Utilities.makeAllCssImportant(appearance.customCss) }} />}

      {/* Video played in background */}
      <video
        ref={videoRef}
        src={'https://cdn.1cdn.app/user/QN1N348R4LLTF1RMH6C2/24071606212410_welcome.mp4'}
        preload="auto"
        playsInline
        autoPlay={false}
        muted
        width="100%"
        height="auto"
        className="tw-w-full tw-h-full tw-aspect-video tw-rounded-2xl tw-object-cover tw-border-solid tw-border-[1px]"
      />

      {/* overlay actions */}
      <div
        className={`tw-absolute tw-top-1 tw-left-0 tw-right-0 tw-bottom-0 tw-p-4 tw-flex tw-flex-col tw-h-full tw-w-full tw-overflow-auto tw-rounded-3xl ${
          isInVideoFailureMode || isLoading ? 'tw-bg-black' : ''
        }`}
      >
        {isLoading ? (
          <div className="tw-w-full tw-h-full tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-2 tw-text-white">
            <svg className="tw-animate-spin tw--ml-1 tw-mr-3 tw-h-5 tw-w-5 tw-text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="tw-opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p>Preparing video...</p>
          </div>
        ) : !isInVideoFailureMode ? (
          <React.Fragment>
            <div className="tw-w-full tw-h-8 tw-justify-start tw-items-center tw-gap-6 tw-inline-flex">
              <button
                id="btn-play-pause"
                type="button"
                onClick={handlePlayPause}
                className="tw-w-8 tw-h-8 tw-border-solid tw-border-gray-300 tw-min-w-8 tw-min-h-8 tw-flex tw-flex-col tw-items-center tw-justify-end tw-py-1 tw-bg-white/20 tw-text-white tw-rounded-[99px] tw-backdrop-blur-md"
              >
                {isPlaying ? <BiPause size={24} /> : <BiPlay size={24} />}
              </button>
              <button
                id="btn-mute-unmute"
                type="button"
                onClick={handleMuteUnmute}
                className="tw-w-8 tw-h-8 tw-border-solid tw-border-gray-300 tw-flex tw-flex-col tw-items-center tw-justify-end tw-p-1 tw-bg-white/20 tw-text-white tw-rounded-[99px] tw-backdrop-blur-md"
              >
                {isMuted || videoRef.current?.muted ? <BiSolidVolumeMute size={18} /> : <BiSolidVolumeFull size={18} />}
              </button>

              <Slider
                defaultValue={0}
                min={0}
                max={Math.floor(duration)}
                value={currentTime}
                onChange={value => handleSeek(value as number)}
                trackStyle={{
                  backgroundColor: widgetUtils.bgColorMapper[appearance.color] ?? appearance.color,
                  height: 8,
                }}
                handleStyle={{
                  borderColor: 'white',
                  height: 20,
                  width: 20,
                  marginLeft: 4,
                  marginTop: -5,
                  borderWidth: 6,
                  backgroundColor: widgetUtils.bgColorMapper[appearance.color] ?? appearance.color,
                }}
                railStyle={{ backgroundColor: '#fff3', height: 8 }}
              />

              {!appearance.hideMinimizeButton && (
                <div className="flex items-center gap-2">
                  {/* Minimize icon */}
                  <button
                    id="btn-position"
                    type="button"
                    disabled
                    className="tw-w-8 tw-h-8 tw-border-solid tw-border-gray-300 tw-flex tw-flex-col tw-items-center tw-justify-end tw-p-1 tw-bg-white/20 tw-text-white tw-rounded-[99px] tw-backdrop-blur-md"
                  >
                    {appearance.position === 'left' ? <RiArrowLeftDownLine size={24} /> : <RiArrowRightDownLine size={24} />}
                  </button>
                </div>
              )}
            </div>
            <div className="tw-w-full tw-flex-col tw-justify-start tw-items-center tw-gap-3 tw-inline-flex tw-mt-auto">
              {showActionButtons && (
                <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                  {(lastMessage && lastMessage.data?.type === 'button' ? (lastMessage.data.value as any[]) : []).map((button: any, index: number) => {
                    if (button.type === 'link' && button.link)
                      // It should also call interaction
                      return (
                        <div
                          key={index}
                          className="tw-self-stretch tw-px-6 tw-py-3 tw-bg-black/50 tw-rounded-2xl tw-border tw-border-black/10 tw-backdrop-blur-md tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"
                        >
                          <img src={button.logo} width={20} height={20} alt={button.label} className="tw-mr-1" />
                          {button.label}
                        </div>
                      );
                    else
                      return (
                        <div
                          key={index}
                          className="tw-self-stretch tw-px-6 tw-py-3 tw-bg-black/50 tw-rounded-2xl tw-border tw-border-black/10 tw-backdrop-blur-md tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"
                        >
                          {button.label}
                        </div>
                      );
                  })}
                </div>
              )}

              {lastMessage && lastMessage.data?.type === 'button' && lastMessage.data.value.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowActionButtons(!showActionButtons)}
                  className="tw-w-8 tw-h-8 tw-border-none tw-min-w-8 tw-min-h-8 tw-p-1 tw-bg-black/20 tw-text-white tw-rounded-[99px] tw-backdrop-blur-md tw-justify-center tw-items-center tw-gap-1 tw-inline-flex"
                  id="voice-btn-element"
                >
                  {showActionButtons ? (
                    <BiChevronsDown size={22} className="tw-animate-pulse" />
                  ) : (
                    <BiChevronsDown size={22} className="tw-animate-pulse tw-rotate-180" />
                  )}
                </button>
              )}

              {(appearance.poweredBy || appearance.customPoweredBy?.isActive) && (
                <div
                  className={`tw-h-5 tw-px-2 tw-py-0.5 tw-bg-black/20 tw-rounded-[99px] tw-backdrop-blur-md tw-text-white tw-items-center tw-gap-0.5 tw-flex tw-justify-center tw-text-center`}
                >
                  <div className="tw-text-[10px] tw-font-normal tw-font-['Lexend'] tw-leading-[15px]">Powered by</div>

                  {appearance.customPoweredBy?.isActive && (
                    <React.Fragment>
                      {appearance.customPoweredBy?.config?.companyLogo &&
                        (appearance.customPoweredBy?.config?.companyUrl?.startsWith('https://') ? (
                          <a href={`${appearance.customPoweredBy?.config?.companyUrl}`}>
                            <img
                              src={appearance.customPoweredBy?.config?.companyLogo}
                              width={14}
                              height={14}
                              alt={appearance.customPoweredBy?.config?.companyName}
                              className="tw-object-contain"
                            />
                          </a>
                        ) : (
                          <img
                            src={appearance.customPoweredBy?.config?.companyLogo}
                            width={14}
                            height={14}
                            alt={appearance.customPoweredBy?.config?.companyName}
                            className="tw-object-contain"
                          />
                        ))}
                      {appearance.customPoweredBy?.config?.companyName &&
                        (appearance.customPoweredBy?.config?.companyUrl?.startsWith('https://') ? (
                          <a href={`${appearance.customPoweredBy?.config?.companyUrl}`}>
                            <span className="tw-text-bold tw-text-[10px] tw-font-['Lexend']">{appearance.customPoweredBy?.config?.companyName}</span>
                          </a>
                        ) : (
                          <span className="tw-text-bold tw-text-[10px] tw-font-['Lexend']">{appearance.customPoweredBy?.config?.companyName}</span>
                        ))}
                    </React.Fragment>
                  )}

                  {appearance.poweredBy && (
                    <a
                      href="https://louisebot.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-text-center tw-flex tw-items-center tw-justify-center tw-gap-1"
                    >
                      <div className="tw-justify-start tw-items-center tw-gap-0.5 tw-flex">
                        <BiLouiseSmallGray fill={'#ffffff'} />
                      </div>
                      <div className="tw-text-bold tw-text-[10px] tw-font-['Lexend']">LouiseBot</div>
                    </a>
                  )}
                </div>
              )}
            </div>
          </React.Fragment>
        ) : (
          <div className="tw-w-full tw-h-full tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-2 tw-text-white">
            <BiSolidVideoOff size={50} />
            <p>Failed to load video... {!isRetrying && <PSButton id="btn-video-retry" type="button" onClick={handleRetry} title="Retry" />}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(VideoWidget);
